import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { REPORTING_LE_LENSES_PATH, reportingLeLensPath } from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type { Lens, LensResponse } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';
import { lensPinsQueryPrefix } from './useLensPinPreferences';

export function useArchivedLenses(enabled: boolean) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...reportingLeLensQueryKeys.lists(), 'archived', user?.email],
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}?archived=true`), {
        signal,
      })) as { lenses: Array<Lens> };
      return response.lenses.filter((lens) => Boolean(lens.archivedAt));
    },
    enabled: enabled && Boolean(user?.email),
    retry: false,
  });
}

export function useSetLensArchived() {
  const cache = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({ lens, archived }: { lens: Lens; archived: boolean }) =>
      request(resolveReportingLeLensUrl(`${reportingLeLensPath(lens.id)}/archive`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived, version: lens.version }),
      }) as Promise<LensResponse>,
    onSuccess: async (_response, { archived }) => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: reportingLeLensQueryKeys.all }),
        cache.invalidateQueries({ queryKey: lensPinsQueryPrefix }),
      ]);
      enqueueSnackbar(
        archived
          ? 'Lens archived. Scheduled emails are paused.'
          : 'Lens restored. Emails remain paused; resume them in Subscriptions.',
        { variant: 'success' },
      );
    },
  });
}
